import { StrictMode, useEffect, useState } from 'react';
import './styles/App.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import Footer from './components/Footer.tsx';
import TemporaryDrawer from './components/TemporaryDrawer.tsx';
import Home from './sections/dashboards/Home.tsx';
import Dashboard from './sections/Dashboard.tsx';
import TariffPage from './sections/TariffPage.tsx';
import AlertsPage from './sections/AlertsPage.tsx';
import AlertContacts from './sections/AlertContacts.tsx';
import AlertRules from './sections/AlertRules.tsx';
import SensorStatus from './sections/SensorStatus.tsx';
import DashboardList from './sections/DashboardList.tsx';
import OverviewDashboard from './sections/dashboards/OverviewDashboard.tsx';
import { Routes, Route } from 'react-router';
import { useOsmNames } from './hooks/useOsmNames.tsx';
import { ProtectedRoute } from './auth/protectedRoute.tsx';
import UnauthorisedPage from './auth/unauthorisedPage.tsx';
import UsersPage from './sections/userManagementPage.tsx';
import CreateUserPage from './sections/createUserPage.tsx';
import EditUserPage from './sections/editUserPage.tsx';
import Callback from './auth/callback.tsx';
import { getUser, isAdmin, getUserGroups } from './auth/authService.ts';
import type { User } from 'oidc-client-ts';
import userManager from './auth/auth.ts';
import DynamicDashboard from './sections/dashboards/DynamicDashboard.tsx';

interface AppProps {
  initialUser: User | null;
}

export function App({ initialUser }: AppProps) {
  const { data: routeList, loading } = useOsmNames();
  const [user, setUser] = useState<User | null>(initialUser);
  const [groups, setGroups] = useState<string[]>([]);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const loadAuth = async () => {
      const u = await userManager.getUser();
      if (u) {
        setUser(u);
        const g = await getUserGroups();
        setGroups(g);
      }
      setAuthLoading(false);
    };

    loadAuth();

    userManager.events.addUserLoaded(async (u) => {
      setUser(u);
      const g = await getUserGroups();
      setGroups(g);
      setAuthLoading(false);
    });

    return () => {
      userManager.events.removeUserLoaded(() => {});
    };
  }, []);

  const admin = groups.includes('admin');

  if (authLoading) return null;

  return (
    <StrictMode>
      <center><div className='main-grid'>
        <TemporaryDrawer user={user} isAdmin={admin} />
        <Routes>
          <Route path='/callback' element={<Callback />} />
          <Route path='/' element={<Dashboard />}>
            <Route index element={<Home />} />
            <Route path='sensor-status'    element={<SensorStatus isAdmin={admin} />} />
            <Route path='dashboard-list'   element={<DashboardList />} />
            <Route
              path='tariffs'
              element={
                <ProtectedRoute user={user} isAdmin={admin} role='admin'>
                  <TariffPage />
                </ProtectedRoute>
              }
            />
            <Route path='overview'         element={<OverviewDashboard />} />
            <Route
              path='alerts'
              element={
                <ProtectedRoute user={user} isAdmin={admin} role='admin'>
                  <AlertsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='alert-rules'
              element={
                <ProtectedRoute user={user} isAdmin={admin} role='admin'>
                  <AlertRules />
                </ProtectedRoute>
              }
            />
            <Route
              path='alert-contacts'
              element={
                <ProtectedRoute user={user} isAdmin={admin} role='admin'>
                  <AlertContacts />
                </ProtectedRoute>
              }
            />
            <Route
              path='users'
              element={
                <ProtectedRoute user={user} isAdmin={admin} role='admin'>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='users/create'
              element={
                <ProtectedRoute user={user} isAdmin={admin} role='admin'>
                  <CreateUserPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='users/edit/:id'
              element={
                <ProtectedRoute user={user} isAdmin={admin} role='admin'>
                  <EditUserPage />
                </ProtectedRoute>
              }
            />
            {routeList.map(route => (
              <Route
                key={route.osm_name}
                path={route.osm_name}
                element={
                  <DynamicDashboard
                    osmName={route.osm_name}
                    isAdmin={admin}
                  />
                }
              />
            ))}
            <Route path='unauthorised' element={<UnauthorisedPage />} />
          </Route>
        </Routes>
      </div></center>
    </StrictMode>
  );
}
